import * as React from "react"
import { ImageOff } from "lucide-react"

import { safeHref } from "@/lib/safe-url"
import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"

import { content as labels, disclosure, imageSummary, missingTags, sampledItems } from "../copy"
import Disclosure from "../Disclosure"
import { FINDING_TONE } from "../severity"
import type { ContentBucket, ContentItem, FindingSeverity, ImageInfo } from "../types"

/*
 * Item links and image addresses are whatever the feed wrote, so one with a
 * scheme safeHref won't vouch for is shown as text rather than linked. It stays
 * on the card because this is a diagnosis: an address that can't be opened is
 * worth seeing.
 */
function ItemUrl({ url, className }: { url: string; className?: string }) {
  const href = safeHref(url)

  if (!href) {
    return <span className={cn("tw:break-all", className)}>{url}</span>
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn("tw:text-primary tw:underline-offset-2 tw:break-all tw:hover:underline", className)}
    >
      {url}
    </a>
  )
}

function hostFromUrl(url?: string): string {
  if (!url) return ""
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return ""
  }
}

/**
 * Prefer an image we know renders, so a broken og:image doesn't leave the card
 * blank when the feed also carried usable media. Falls back to the unreachable
 * og:image last so the card still links to what the publisher declared.
 */
function pickPreviewImage(item: ContentItem): ImageInfo | null {
  if (item.image?.reachable && item.image.url) return item.image
  const usableFeedMedia = (item.feedMedia ?? []).find((m) => m.reachable && m.url)
  if (usableFeedMedia) return usableFeedMedia
  if (item.image?.url) return item.image
  return null
}

function ImageDescriptor({ label, image }: { label: string; image?: ImageInfo }) {
  if (!image) return null

  const kb = Math.round((Number(image.bytes) || 0) / 1024)
  const width = Number(image.width) || 0
  const height = Number(image.height) || 0
  const dimensions = width > 0 && height > 0 ? `${width}×${height}` : labels.unknownDimensions

  return (
    <div className="tw:py-0.5 tw:text-xs">
      <span className="tw:text-muted-foreground">{label}: </span>
      {image.url ? (
        <ItemUrl url={image.url} />
      ) : (
        <span>{labels.noUrl}</span>
      )}
      {" — "}
      {image.reachable ? (
        <span>{imageSummary(dimensions, image.format ?? "", kb)}</span>
      ) : (
        <span className="tw:font-medium tw:text-destructive">{labels.unreachable}</span>
      )}
    </div>
  )
}

/* Takes a severity rather than its own colour vocabulary, so the chips can't
 * drift from the finding rows and section bands they sit beside. */
function Chip({
  severity,
  children,
}: {
  severity: FindingSeverity
  children: React.ReactNode
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "tw:h-auto tw:py-0.5 tw:whitespace-normal",
        FINDING_TONE[severity].chip
      )}
    >
      {children}
    </Badge>
  )
}

function ContentItemCard({ item }: { item: ContentItem }) {
  const preview = pickPreviewImage(item)
  const previewUrl = preview?.thumbUrl ?? preview?.url
  const previewBroken = preview ? !preview.reachable : false

  const description = item.og?.description || item.twitter?.description || ""
  const domain = hostFromUrl(item.link)
  const missing = item.missing ?? []
  const hasFeedMedia = (item.feedMedia ?? []).length > 0

  return (
    <li className="tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:p-3">
      <div
        className={cn(
          "tw:flex tw:size-20 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-muted",
          previewBroken && "tw:border-destructive/40"
        )}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="tw:size-full tw:object-cover"
          />
        ) : (
          <ImageOff
            aria-label={labels.noImageLabel}
            className="tw:size-5 tw:text-muted-foreground"
          />
        )}
      </div>

      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
        {domain && (
          <div className="tw:text-xs tw:tracking-wide tw:text-muted-foreground tw:uppercase">
            {domain}
          </div>
        )}
        <div className="tw:text-sm tw:font-semibold">
          {item.title || item.og?.title || labels.noTitle}
        </div>
        {description && (
          <div className="tw:text-sm tw:text-muted-foreground">{description}</div>
        )}
        {item.link && <ItemUrl url={item.link} className="tw:text-xs" />}

        {/*
          * Status chips share a row; the missing-tags chip gets its own. Its
          * text grows with the number of absent tags, so on one line it ran
          * roughly four times the width of its neighbours and forced a ragged
          * wrap that made the short chips hard to scan.
          */}
        {(!item.pageFetched ||
          item.cloudflareChallenge ||
          item.accessibleViaProxyOnly ||
          (item.image && !item.image.reachable)) && (
          <div className="tw:mt-1 tw:flex tw:flex-wrap tw:gap-1.5">
            {!item.pageFetched &&
              (item.proxySkipped ? (
                <Chip severity="warning">{labels.chipSkipped}</Chip>
              ) : (
                <Chip severity="critical">{labels.chipPageUnreachable}</Chip>
              ))}
            {item.cloudflareChallenge && <Chip severity="warning">{labels.chipCloudflare}</Chip>}
            {item.accessibleViaProxyOnly && <Chip severity="warning">{labels.chipProxyOnly}</Chip>}
            {item.image && !item.image.reachable && (
              <Chip severity="critical">{labels.chipImageUnreachable}</Chip>
            )}
          </div>
        )}
        {missing.length > 0 && (
          <div className="tw:mt-1 tw:flex">
            <Chip severity="warning">{missingTags(missing)}</Chip>
          </div>
        )}

        {(item.image || hasFeedMedia) && (
          <Disclosure label={disclosure.rawImages} className="tw:mt-1">
            <ImageDescriptor label={labels.ogImage} image={item.image} />
            {(item.feedMedia ?? []).map((media, idx) => (
              <ImageDescriptor key={`fm-${idx}`} label={labels.feedMedia} image={media} />
            ))}
          </Disclosure>
        )}
      </div>
    </li>
  )
}

export default function ContentDetails({ bucket }: { bucket: ContentBucket }) {
  const items = bucket.items ?? []
  if (items.length === 0) {
    return <p className="tw:text-sm tw:text-muted-foreground">{bucket.summary}</p>
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:text-muted-foreground">
        {sampledItems(bucket.itemsSampled ?? items.length, bucket.itemsTotal ?? items.length)}
      </p>
      <ul className="tw:flex tw:flex-col tw:gap-2">
        {items.map((item, idx) => (
          <ContentItemCard key={item.link ?? idx} item={item} />
        ))}
      </ul>
    </div>
  )
}
